// =============================================================================
// PRÉPARATION DES DOCUMENTS POUR SIGNATURE
// =============================================================================
// Workflow en 2 étapes :
// ÉTAPE 1 : Sélectionner et GÉNÉRER les documents (remplir les variables)
// ÉTAPE 2 : Une fois générés → Télécharger / Imprimer / Envoyer à DocuSign
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  FileText,
  Download,
  Printer,
  CheckSquare,
  Square,
  FileSignature,
  Calendar,
  MapPin,
  User,
  Building2,
  ChevronDown,
  ChevronRight,
  Loader2,
  Eye,
  Send,
  AlertCircle,
  CheckCircle2,
  Clock,
  RefreshCw,
  FileCheck,
  PenTool,
  Sparkles,
  Mail,
  ExternalLink,
  Package
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// CONFIGURATION DES DOCUMENTS PAR PHASE
// =============================================================================

// =============================================================================
// LISTES DE DOCUMENTS — TENANT CLIMAT (CEE)
// =============================================================================

const DOCUMENTS_AVANT_TRAVAUX_CLIMAT = [
  {
    id: "devis",
    code: "DEVIS",
    nom: "Devis",
    description: "Devis signé par le client",
    source: "devis",
    champsSignature: [
      { type: "signature", label: "Signature client", position: "bas-gauche" },
      { type: "date", label: "Date de signature", position: "bas-gauche" },
      { type: "lieu", label: "Fait à", position: "bas-gauche" },
      { type: "mention", label: "Lu et approuvé, Bon pour accord", position: "bas-gauche" }
    ]
  },
  {
    id: "mandat_mpr",
    code: "MANDAT_MPR",
    nom: "Mandat ANAH / MPR",
    description: "Cerfa officiel MaPrimeRénov' / MaPrimeAdapt' — rempli + tampon mandataire",
    source: "modele_officiel",
    // Aliases pour matcher les codes templates DB selon le tenant
    template_match: ["MANDAT_MPR", "MANDAT_ANAH"],
    type_match: ["mandat_mpr", "mandat_anah"],
    champsSignature: [
      { type: "signature", label: "Signature mandant (client)", position: "bas-gauche" },
      { type: "signature", label: "Signature mandataire (installateur)", position: "bas-droite" },
      { type: "date", label: "Date", position: "bas-centre" },
      { type: "lieu", label: "Fait à", position: "haut" }
    ]
  },
  {
    id: "cadre_contribution",
    code: "CC_CEE",
    nom: "Cadre de Contribution CEE",
    description: "Cadre de contribution du délégataire",
    source: "template",
    champsSignature: [
      { type: "signature", label: "Signature bénéficiaire", position: "page-5" },
      { type: "date", label: "Date d'engagement", position: "page-5" },
      { type: "lieu", label: "Fait à", position: "page-5" },
      { type: "paraphe", label: "Paraphes (toutes pages)", position: "bas-droit" }
    ]
  },
  {
    id: "indivision_mpr",
    code: "INDIVISION_MPR",
    nom: "Attestation d'Indivision MPR",
    description: "Pour les propriétaires en indivision",
    source: "modele_officiel",
    optionnel: true,
    champsSignature: [
      { type: "signature", label: "Signature tous indivisaires", position: "bas" },
      { type: "date", label: "Date", position: "bas" }
    ]
  },
  {
    id: "consentement_mpr",
    code: "CONSENTEMENT_MPR",
    nom: "Consentement Conjoint MPR",
    description: "Consentement du conjoint si nécessaire",
    source: "modele_officiel",
    optionnel: true,
    champsSignature: [
      { type: "signature", label: "Signature conjoint", position: "bas" },
      { type: "date", label: "Date", position: "bas" }
    ]
  }
];

const DOCUMENTS_APRES_TRAVAUX_CLIMAT = [
  {
    id: "attestation_honneur",
    code: "AH_CEE",
    nom: "Attestation sur l'Honneur CEE",
    description: "Attestation sur l'honneur du bénéficiaire",
    source: "template",
    champsSignature: [
      { type: "signature", label: "Signature bénéficiaire", position: "bas" },
      { type: "signature", label: "Signature installateur", position: "bas" },
      { type: "date", label: "Date", position: "bas" },
      { type: "lieu", label: "Fait à", position: "bas" }
    ]
  },
  {
    id: "attestation_mise_service",
    code: "AMES",
    nom: "Attestation de Mise en Service",
    description: "Attestation de mise en service de l'équipement",
    source: "template",
    champsSignature: [
      { type: "signature", label: "Signature client", position: "bas-gauche" },
      { type: "signature", label: "Signature technicien", position: "bas-droite" },
      { type: "date", label: "Date de mise en service", position: "haut" }
    ]
  },
  {
    id: "attestation_surface",
    code: "ASC_CEE",
    nom: "Attestation Surface Chauffée",
    description: "Attestation de surface chauffée",
    source: "template",
    champsSignature: [
      { type: "signature", label: "Signature bénéficiaire", position: "bas" },
      { type: "date", label: "Date", position: "bas" }
    ]
  },
  {
    id: "fiche_standardisee",
    code: "FICHE_STD",
    nom: "Fiche Standardisée",
    description: "Fiche d'opération standardisée CEE",
    source: "template",
    champsSignature: [
      { type: "signature", label: "Signature installateur RGE", position: "bas" },
      { type: "date", label: "Date", position: "bas" }
    ]
  }
];

// =============================================================================
// LISTES DE DOCUMENTS — TENANT TEF (MaPrimeAdapt')
// =============================================================================

// Mapping type_document metier_devis (TEF natif) -> shape attendu par
// isDocumentGenere/getDocumentGenere : template_code matche les
// template_match TEF, type_document matche les type_match, slug est
// l'endpoint download natif TEF (/api/metier/tef/dossiers/{id}/{slug}/download).
const TEF_AMO_DOC_MAP = {
  devis_amo:   { template_code: "DEVIS_AMO_TEF_2025",   type_document: "devis_amo",   slug: "devis-amo" },
  contrat_amo: { template_code: "CONTRAT_AMO_TEF_2025", type_document: "contrat_amo", slug: "contrat-amo" },
  mandat_amo:  { template_code: "MANDAT_ANAH_TEF_2025", type_document: "mandat_anah", slug: "mandat-amo" },
};

const DOCUMENTS_AVANT_TRAVAUX_TEF = [
  {
    id: "devis_amo",
    code: "DEVIS_AMO",
    nom: "Devis AMO",
    description: "Devis de l'Assistance à Maîtrise d'Ouvrage signé par le client",
    source: "devis",
    template_match: ["DEVIS_AMO"],
    type_match: ["devis_amo"],
    champsSignature: [
      { type: "signature", label: "Signature client", position: "bas-gauche" },
      { type: "date", label: "Date de signature", position: "bas-gauche" },
      { type: "lieu", label: "Fait à", position: "bas-gauche" },
      { type: "mention", label: "Lu et approuvé, Bon pour accord", position: "bas-gauche" }
    ]
  },
  {
    id: "contrat_amo",
    code: "CONTRAT_AMO",
    nom: "Contrat AMO",
    description: "Contrat d'Assistance à Maîtrise d'Ouvrage (CGV 13 articles)",
    source: "devis",
    template_match: ["CONTRAT_AMO"],
    type_match: ["contrat_amo"],
    champsSignature: [
      { type: "signature", label: "Signature client", position: "bas-gauche" },
      { type: "signature", label: "Signature mandataire (TEF)", position: "bas-droite" },
      { type: "date", label: "Date de signature", position: "bas" },
      { type: "lieu", label: "Fait à", position: "bas" }
    ]
  },
  {
    id: "mandat_anah",
    code: "MANDAT_ANAH",
    nom: "Mandat ANAH",
    description: "Mandat ANAH pour MaPrimeAdapt' — apposé tampon + signature mandataire",
    source: "modele_officiel",
    cerfa_source: "mandat_anah_mpa",
    template_match: ["MANDAT_MPR", "MANDAT_ANAH"],
    type_match: ["mandat_mpr", "mandat_anah"],
    champsSignature: [
      { type: "signature", label: "Signature mandant (client)", position: "bas-gauche" },
      { type: "signature", label: "Signature mandataire (installateur)", position: "bas-droite" },
      { type: "date", label: "Date", position: "bas-centre" },
      { type: "lieu", label: "Fait à", position: "haut" }
    ]
  },
  {
    id: "plan_financement",
    code: "PLAN_FINANCEMENT",
    nom: "Plan de Financement",
    description: "Cerfa 13460-03 — Plan de financement définitif",
    source: "modele_officiel",
    cerfa_source: "cerfa_13460",
    champsSignature: [
      { type: "signature", label: "Signature demandeur", position: "bas" },
      { type: "date", label: "Date", position: "bas" }
    ]
  },
  {
    id: "demande_avance_anah",
    code: "DEMANDE_AVANCE_ANAH",
    nom: "Demande d'Avance ANAH",
    description: "Cerfa 13934-04 — Demande d'avance de subvention ANAH",
    source: "modele_officiel",
    cerfa_source: "cerfa_13934",
    champsSignature: [
      { type: "signature", label: "Signature demandeur", position: "bas" },
      { type: "date", label: "Date", position: "bas" }
    ]
  },
  {
    id: "demande_demarrage_anticipe",
    code: "DEMANDE_DEMARRAGE_ANTICIPE",
    nom: "Demande de démarrage anticipé",
    description: "Demande expresse de démarrage des travaux avant accord ANAH",
    source: "modele_officiel",
    cerfa_source: "demande_demarrage_anticipe",
    champsSignature: [
      { type: "signature", label: "Signature demandeur", position: "bas" },
      { type: "date", label: "Date", position: "bas" },
      { type: "lieu", label: "Fait à", position: "bas" }
    ]
  },
  {
    id: "bordereau_retractation",
    code: "BORDEREAU_RETRACTATION",
    nom: "Bordereau de rétractation",
    description: "Bordereau de rétractation 14 jours (Code de la Consommation)",
    source: "template",
    template_match: ["BORDEREAU_RETRACTATION"],
    type_match: ["bordereau_retractation"],
    champsSignature: [
      { type: "signature", label: "Signature client (si rétractation)", position: "bas" },
      { type: "date", label: "Date", position: "bas" }
    ]
  }
];

const DOCUMENTS_APRES_TRAVAUX_TEF = [
  {
    id: "procuration_seing_prive",
    code: "PROCURATION_SEING_PRIVE",
    nom: "Procuration sous seing privé",
    description: "Cerfa 13463-02 — Procuration de perception des fonds",
    source: "modele_officiel",
    cerfa_source: "cerfa_13463",
    champsSignature: [
      { type: "signature", label: "Signature mandant", position: "bas" },
      { type: "signature", label: "Signature mandataire", position: "bas" },
      { type: "date", label: "Date", position: "bas" },
      { type: "lieu", label: "Fait à", position: "bas" }
    ]
  }
];

// Icônes pour les types de champs de signature
const SIGNATURE_ICONS = {
  signature: PenTool,
  date: Calendar,
  lieu: MapPin,
  mention: FileText,
  paraphe: FileSignature
};

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export default function SignaturePreparation({ 
  devisId, 
  devis, 
  dossier, 
  tenantId, 
  isTEF: isTEFProp,
  onRefresh 
}) {
  // Source de vérité tenant pour les documents : prop > dossier.tenant_id
  // isTEF = TEF/MaPrimeAdapt' (liste documents différente de Climat/CEE)
  const isTEF = isTEFProp ?? (tenantId === "tef" || dossier?.tenant_id === "tef");

  // Listes de documents selon le tenant (TEF/MPA vs Climat/CEE)
  const docsAvantTravaux = isTEF ? DOCUMENTS_AVANT_TRAVAUX_TEF : DOCUMENTS_AVANT_TRAVAUX_CLIMAT;
  const docsApresTravaux = isTEF ? DOCUMENTS_APRES_TRAVAUX_TEF : DOCUMENTS_APRES_TRAVAUX_CLIMAT;

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sendingSignature, setSendingSignature] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [documentsGeneres, setDocumentsGeneres] = useState([]);
  
  // Documents officiels générés (PDFs remplis)
  const [pdfsGeneres, setPdfsGeneres] = useState({});
  
  // Sélection des documents
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [expandedPhase, setExpandedPhase] = useState("avant_travaux");
  
  // Dialog de génération
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [docsToGenerate, setDocsToGenerate] = useState([]);
  
  // Dialog DocuSign
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [signatureForm, setSignatureForm] = useState({
    signerEmail: "",
    signerName: "",
    emailSubject: "Documents à signer",
    emailMessage: ""
  });
  const [docToSign, setDocToSign] = useState(null);
  
  // État DocuSign
  const [docusignConfigured, setDocusignConfigured] = useState(false);
  const [signatureStatus, setSignatureStatus] = useState(null);

  // Fonction pour obtenir les headers avec authentification (toujours frais)
  const getAuthHeaders = (contentType = "application/json") => {
    const token = localStorage.getItem("session_token");
    if (!tenantId) {
      console.error("[SignaturePreparation] tenantId manquant — impossible d'appeler l'API.");
    }
    return {
      "X-Tenant-ID": tenantId,
      ...(contentType && { "Content-Type": contentType }),
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  // Délégataire du dossier
  const delegataireDossier = dossier?.delegataire || devis?.delegataire || "";

  // ==========================================================================
  // CHARGEMENT DES DONNÉES
  // ==========================================================================

  const loadData = useCallback(async () => {
    if (!devisId) return;
    
    setLoading(true);
    try {
      const authHeaders = getAuthHeaders();

      const [templatesRes, documentsRes, docusignRes] = await Promise.all([
        fetch(`${API}/api/metier/documents-engine/templates`, { headers: authHeaders }),
        fetch(`${API}/api/metier/documents-engine/devis/${devisId}/documents`, { headers: authHeaders }),
        fetch(`${API}/api/docusign/config`, { headers: authHeaders }).catch(() => null)
      ]);

      if (templatesRes.ok) {
        const data = await templatesRes.json();
        // Filtrer les templates par délégataire si défini
        const filtered = delegataireDossier 
          ? data.filter(t => !t.delegataire || t.delegataire === delegataireDossier)
          : data;
        setTemplates(filtered);
      }

      let docsList = [];
      if (documentsRes.ok) {
        const data = await documentsRes.json();
        docsList = Array.isArray(data) ? data : [];
      }

      // Pour TEF, fusionner les devis_amo/contrat_amo/mandat_amo natifs
      // (collection metier_devis) dans documentsGeneres : sinon
      // isDocumentGenere reste false pour ces 3 types et seul "Cerfa vierge"
      // (CERFA blanc non rempli) reste affiche cote UI.
      if (isTEF && dossier?.id) {
        try {
          const tefRes = await fetch(
            `${API}/api/metier/tef/dossiers/${dossier.id}/devis-contrats`,
            { headers: authHeaders }
          );
          if (tefRes.ok) {
            const tefJson = await tefRes.json();
            const tefDocs = (tefJson?.documents || [])
              .map(d => {
                const map = TEF_AMO_DOC_MAP[d.type_document];
                if (!map) return null;
                return {
                  id: d.id,
                  template_code: map.template_code,
                  type_document: map.type_document,
                  download_url: `/api/metier/tef/dossiers/${dossier.id}/${map.slug}/download`,
                  source: "metier_devis",
                  generated_at: d.created_at,
                };
              })
              .filter(Boolean);
            // prepend : en cas de collision avec documents-engine, gagne la
            // version metier_devis (= source de verite TEF natif)
            docsList = [...tefDocs, ...docsList];
          }
        } catch (err) {
          console.warn("[SignaturePreparation] fusion docs TEF metier_devis échouée:", err);
        }
      }
      setDocumentsGeneres(docsList);

      // Vérifier si DocuSign est configuré
      if (docusignRes && docusignRes.ok) {
        const config = await docusignRes.json();
        setDocusignConfigured(config.status === "configured");
        
        // Si configuré et qu'on a un devisId, récupérer le statut de signature
        if (config.status === "configured" && devisId) {
          try {
            const statusRes = await fetch(`${API}/api/docusign/devis/${devisId}/signature-status`, { headers: authHeaders });
            if (statusRes.ok) {
              const statusData = await statusRes.json();
              setSignatureStatus(statusData);
            }
          } catch (err) {
            console.warn("Impossible de récupérer le statut DocuSign:", err);
          }
        }
      }
      
      // Pré-remplir le formulaire de signature avec les infos client
      if (dossier || devis) {
        const clientEmail = dossier?.client_email || devis?.client_email || "";
        const clientNom = dossier?.client_nom || devis?.client_nom || "";
        const clientPrenom = dossier?.client_prenom || devis?.client_prenom || "";
        setSignatureForm(prev => ({
          ...prev,
          signerEmail: clientEmail,
          signerName: `${clientPrenom} ${clientNom}`.trim() || clientNom
        }));
      }
    } catch (error) {
      console.error("Erreur chargement:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [devisId, tenantId, delegataireDossier, dossier, devis]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ==========================================================================
  // GESTION DE LA SÉLECTION
  // ==========================================================================

  const toggleDocSelection = (docId) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocs(newSelected);
  };

  const selectAllPhase = (phase) => {
    const docs = phase === "avant_travaux" ? docsAvantTravaux : docsApresTravaux;
    const newSelected = new Set(selectedDocs);
    docs.forEach(doc => {
      if (!doc.optionnel) {
        newSelected.add(doc.id);
      }
    });
    setSelectedDocs(newSelected);
  };

  const deselectAllPhase = (phase) => {
    const docs = phase === "avant_travaux" ? docsAvantTravaux : docsApresTravaux;
    const newSelected = new Set(selectedDocs);
    docs.forEach(doc => newSelected.delete(doc.id));
    setSelectedDocs(newSelected);
  };

  // ==========================================================================
  // GÉNÉRATION DES DOCUMENTS
  // ==========================================================================

  const handlePrepareGeneration = () => {
    if (selectedDocs.size === 0) {
      toast.error("Sélectionnez au moins un document");
      return;
    }

    const allDocs = [...docsAvantTravaux, ...docsApresTravaux];
    const docsSelected = allDocs.filter(doc => selectedDocs.has(doc.id));
    setDocsToGenerate(docsSelected);
    setShowGenerateDialog(true);
  };

  // ==========================================================================
  // GÉNÉRATION PDF OFFICIELS (Mandat MPR, Attestations)
  // ==========================================================================

  const generateOfficialPDF = async (doc, authHeaders) => {
    // Le devis contient déjà les données client et adresse travaux
    // Structure: devis.client_nom, devis.adresse_travaux, etc.
    
    // DEBUG CRITIQUE: Afficher les props reçues
    console.log("[PDF Generation] ===== DÉBUT GÉNÉRATION =====");
    console.log("[PDF Generation] Document:", doc.code);
    console.log("[PDF Generation] devis prop:", devis);
    console.log("[PDF Generation] dossier prop:", dossier);
    
    try {
      let endpoint = "";
      let payload = {};
      
      // DEBUG: Afficher les props reçues pour diagnostic
      console.log("[PDF Generation] Props reçues:", {
        devis: devis ? {
          id: devis.id,
          client_nom: devis.client_nom,
          client_email: devis.client_email,
          client_telephone: devis.client_telephone,
          client_adresse: devis.client_adresse,
          adresse_travaux: devis.adresse_travaux,
          client_civilite: devis.client_civilite
        } : "null",
        dossier: dossier ? {
          id: dossier.id,
          client_nom: dossier.client_nom,
          adresse_travaux: dossier.adresse_travaux,
          adresse_projet: dossier.adresse_projet
        } : "null"
      });
      
      // Récupérer les données du mandataire par défaut depuis la nouvelle API
      // 🔴 #761 — CE REPLI PORTAIT DES DONNÉES RÉELLES. Il nommait une personne
      // physique et l'identité COMPLÈTE d'un AMO client — raison sociale,
      // adresse, e-mail, téléphone. ⚠️ Servant de repli quand l'API ne répond
      // pas, il pouvait faire figurer ce mandataire sur une PRÉPARATION DE
      // SIGNATURE, c'est-à-dire sur un document réel destiné à un autre tenant.
      // ⛔ Vidé : si l'API ne répond pas, les champs restent VIDES — un manque
      // se voit, une identité fausse ne se voit pas.
      // ⚠️ Le vrai correctif — REFUSER plutôt que se replier — touche la chaîne
      // de signature : lot distinct, signalé.
      let mandataireData = {
        civilite: "",
        nom: "",
        prenom: "",
        raison_sociale: "",
        adresse: "",
        code_postal: "",
        ville: "",
        email: "",
        telephone: ""
      };
      
      try {
        // Nouvelle API - collection mandataires séparée
        const mandRes = await fetch(`${API}/api/platform/mandataires/default`, {
          headers: authHeaders
        });
        if (mandRes.ok) {
          const mandJson = await mandRes.json();
          if (mandJson && mandJson.raison_sociale) {
            mandataireData = mandJson;
          }
        }
      } catch (err) {
        console.warn("Utilisation des données mandataire par défaut");
      }
      
      // Récupérer les données du devis - avec fallback robuste
      const clientNomComplet = devis?.client_nom || dossier?.client_nom || "";
      const clientParts = clientNomComplet.trim().split(" ");
      const clientPrenom = clientParts.length > 1 ? clientParts[0] : "";
      const clientNom = clientParts.length > 1 ? clientParts.slice(1).join(" ") : clientNomComplet;
      
      // Adresse travaux avec fallback sur adresse_projet du dossier
      const adresseTravaux = devis?.adresse_travaux || dossier?.adresse_travaux || dossier?.adresse_projet || {};
      const clientAdresse = devis?.client_adresse || dossier?.client_adresse || {};
      
      // DEBUG: Log des données extraites
      console.log("[PDF Generation] Données extraites:", {
        clientNomComplet,
        clientPrenom,
        clientNom,
        adresseTravaux,
        clientAdresse
      });

      // Pour TEF : le Mandat ANAH ne passe pas par DocuSign — TEF signe en
      // tant que mandataire. Route vers l'endpoint dédié qui lit
      // donnees_mpr.mandataire_id, remplit le Cerfa 15923*01 et persiste en
      // metier_devis (type_document=mandat_amo). Le doc apparaîtra ensuite
      // automatiquement dans "Documents générés" et ici en lecture seule.
      // Cf. AUDIT_MANDATAIRE.md §5.2 + tef/routes.py:api_generer_mandat_amo.
      if (isTEF && (doc.code === "MANDAT_ANAH" || doc.code === "MANDAT_MPR")) {
        const dossierIdLocal = dossier?.id || devisId;
        try {
          const res = await fetch(
            `${API}/api/metier/tef/dossiers/${dossierIdLocal}/mandat-amo/generer`,
            { method: "POST", headers: authHeaders }
          );
          if (res.ok) {
            const result = await res.json();
            return {
              success: true,
              download_url:
                result?.mandat?.download_url
                || `/api/metier/tef/dossiers/${dossierIdLocal}/mandat-amo/download`,
            };
          }
          const errData = await res.json().catch(() => ({}));
          return {
            success: false,
            error: errData.detail?.message || errData.detail || "Erreur génération Mandat ANAH",
          };
        } catch (error) {
          return { success: false, error: error.message };
        }
      }

      switch (doc.code) {
        case "MANDAT_MPR":
          endpoint = "/api/pdf/fill/mandat-mpr";
          payload = {
            demandeur: {
              civilite: devis?.client_civilite || dossier?.client_civilite || "M.",
              nom: clientNom,
              prenom: clientPrenom,
              email: devis?.client_email || dossier?.client_email || "",
              telephone: devis?.client_telephone || dossier?.client_telephone || "",
              est_proprietaire: true
            },
            mandataire: {
              id: mandataireData.id,
              mandataire_id: mandataireData.id,
              civilite: mandataireData.civilite || "M.",
              nom: mandataireData.nom,
              prenom: mandataireData.prenom,
              raison_sociale: mandataireData.raison_sociale,
              adresse: mandataireData.adresse,
              code_postal: mandataireData.code_postal,
              ville: mandataireData.ville,
              email: mandataireData.email,
              telephone: mandataireData.telephone,
              signature_url: mandataireData.signature_url,
              signature_path: mandataireData.signature_url,
              tampon_url: mandataireData.tampon_url,
              tampon_path: mandataireData.tampon_url
            },
            logement: {
              adresse: adresseTravaux.rue || adresseTravaux.adresse || clientAdresse.rue || "",
              code_postal: adresseTravaux.code_postal || clientAdresse.code_postal || "",
              ville: adresseTravaux.ville || clientAdresse.ville || ""
            },
            mandat_type: {
              administratif: true,
              financier: true
            },
            signature: {
              lieu: adresseTravaux.ville || clientAdresse.ville || mandataireData.ville || "",
              date: new Date().toISOString().split("T")[0]
            },
            dossier: {
              numero_mpr_1: dossier?.numero_mpr?.split("-")?.[0] || "",
              numero_mpr_2: dossier?.numero_mpr?.split("-")?.[1] || ""
            },
            dossier_id: dossier?.id || devisId
          };
          break;

        case "ATTESTATION_INDIVISION":
          endpoint = "/api/pdf/fill/attestation-indivision";
          payload = {
            representant: {
              identite: clientNomComplet,
              adresse: `${adresseTravaux.rue || ""}, ${adresseTravaux.code_postal || ""} ${adresseTravaux.ville || ""}`.trim().replace(/^,\s*/, ""),
              nom_signature: clientNomComplet
            },
            bien: {
              adresse: `${adresseTravaux.rue || ""}, ${adresseTravaux.code_postal || ""} ${adresseTravaux.ville || ""}`.trim().replace(/^,\s*/, "")
            },
            signature: {
              lieu: adresseTravaux.ville || "",
              date: new Date().toISOString().split("T")[0]
            },
            dossier: {
              numero_mpr: dossier?.numero_mpr || ""
            },
            dossier_id: dossier?.id || devisId
          };
          break;

        case "CONSENTEMENT_CONJOINT":
          endpoint = "/api/pdf/fill/attestation-consentement";
          payload = {
            demandeur: {
              nom_complet: clientNomComplet,
              adresse_ligne1: adresseTravaux.rue || clientAdresse.rue || "",
              adresse_ligne2: `${adresseTravaux.code_postal || clientAdresse.code_postal || ""} ${adresseTravaux.ville || clientAdresse.ville || ""}`.trim()
            },
            logement: {
              adresse_ligne1: adresseTravaux.rue || "",
              adresse_ligne2: `${adresseTravaux.code_postal || ""} ${adresseTravaux.ville || ""}`.trim()
            },
            travaux: devis?.lignes?.map(l => ({
              description: l.snapshot?.designation || l.designation || "",
              montant: String(l.montant_ttc || 0)
            })) || [],
            // 🔴 #761 — le nom d'un AMO client etait EN DUR dans le bloc
            // entreprise du mandat. ⛔ Vide : un champ manquant se voit sur le
            // document, une entreprise fausse ne se voit pas.
            entreprises: [{
              nom: "",
              siret: ""
            }],
            montants: {
              total_prime: String(devis?.montant_mpr || devis?.aides?.mpr || 0),
              reste_a_charge: String(devis?.reste_a_charge || devis?.montants?.reste_a_charge || 0)
            },
            mandat: {
              administratif: true,
              financier: true,
              mixte: false
            },
            conditions: {
              compte_cree: true,
              renovation_logement: true,
              connait_numero: true,
              prime_acceptee: true,
              par_entreprises: true,
              montant_accepte: true,
              reste_a_charge: true
            },
            signature: {
              lieu: adresseTravaux.ville || "",
              date: new Date().toLocaleDateString("fr-FR"),
              nom_complet: clientNomComplet
            },
            dossier: {
              numero_mpr: dossier?.numero_mpr || ""
            },
            dossier_id: dossier?.id || devisId
          };
          break;

        default:
          return { success: false, error: `Type de document non supporté: ${doc.code}` };
      }

      console.log(`Génération PDF ${doc.code}:`, JSON.stringify(payload, null, 2));

      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const result = await res.json();
        
        // Enregistrer le PDF officiel dans documents_generes pour qu'il apparaisse dans l'onglet
        try {
          await fetch(`${API}/api/metier/documents-engine/devis/${devisId}/pdf-officiels`, {
            method: "POST",
            headers: authHeaders,
            body: JSON.stringify({
              template_code: doc.code,
              download_url: result.download_url,
              hash_sha256: result.hash_sha256,
              phase: "avant_travaux"
            })
          });
        } catch (err) {
          console.warn("Erreur enregistrement PDF officiel:", err);
        }
        
        return { 
          success: true, 
          download_url: result.download_url,
          hash: result.hash_sha256 
        };
      } else {
        const errData = await res.json().catch(() => ({}));
        console.error("Erreur PDF:", errData);
        return { 
          success: false, 
          error: errData.detail?.message || errData.detail || "Erreur de remplissage PDF" 
        };
      }
    } catch (error) {
      console.error(`Erreur génération PDF ${doc.code}:`, error);
      return { success: false, error: error.message };
    }
  };

  const handleGenerateAll = async () => {
    setGenerating(true);
    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    const authHeaders = getAuthHeaders();

    for (const doc of docsToGenerate) {
      try {
        if (doc.source === "template") {
          // Documents avec template HTML → génération via documents-engine
          const template = templates.find(t => 
            t.code.includes(doc.code) || 
            t.type_document === doc.code.toLowerCase()
          );

          if (template) {
            const res = await fetch(
              `${API}/api/metier/documents-engine/generer/${devisId}?mode=unitaire&template_code=${template.code}`,
              {
                method: "POST",
                headers: authHeaders
              }
            );

            if (res.ok) {
              const result = await res.json();
              if (result.success && result.documents?.length > 0) {
                successCount++;
              } else if (result.erreurs?.length > 0) {
                errorCount++;
                errors.push(`${doc.nom}: ${result.erreurs[0]}`);
              } else {
                successCount++;
              }
            } else {
              const errData = await res.json().catch(() => ({}));
              errorCount++;
              errors.push(`${doc.nom}: ${errData.detail || "Erreur de génération"}`);
            }
          }
        } else if (doc.source === "modele_officiel") {
          // Documents officiels (PDF modifiables) → remplissage via pdf-form-filler
          const pdfResult = await generateOfficialPDF(doc, authHeaders);
          if (pdfResult.success) {
            successCount++;
            // Stocker le PDF généré pour afficher les boutons
            setPdfsGeneres(prev => ({
              ...prev,
              [doc.code]: {
                download_url: pdfResult.download_url,
                hash: pdfResult.hash,
                generated_at: new Date().toISOString()
              }
            }));
          } else {
            errorCount++;
            errors.push(`${doc.nom}: ${pdfResult.error}`);
          }
        } else if (doc.source === "devis") {
          // Le devis existe déjà - stocker l'URL pour afficher les boutons d'action
          // L'URL du PDF du devis est disponible via l'API documents
          const devisDownloadUrl = `${API}/api/metier/documents/devis/${devisId}/pdf`;
          
          setPdfsGeneres(prev => ({
            ...prev,
            [doc.code]: {
              download_url: devisDownloadUrl,
              source: "devis",
              generated_at: new Date().toISOString()
            }
          }));
          successCount++;
        }
      } catch (error) {
        console.error(`Erreur génération ${doc.nom}:`, error);
        errorCount++;
        errors.push(`${doc.nom}: ${error.message}`);
      }
    }

    setGenerating(false);
    setShowGenerateDialog(false);

    if (successCount > 0) {
      toast.success(`${successCount} document(s) généré(s) avec succès`);
      loadData();
      if (onRefresh) onRefresh();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} document(s) en erreur`, {
        description: errors.slice(0, 2).join(", ")
      });
    }
  };

  // ==========================================================================
  // TÉLÉCHARGEMENT / IMPRESSION
  // ==========================================================================

  const handleDownloadAll = async () => {
    if (selectedDocs.size === 0) {
      toast.error("Sélectionnez au moins un document");
      return;
    }

    // Télécharger les documents générés qui correspondent à la sélection
    for (const docGen of documentsGeneres) {
      const allDocs = [...docsAvantTravaux, ...docsApresTravaux];
      const configDoc = allDocs.find(d => 
        docGen.template_code?.includes(d.code) || d.id === docGen.type_document
      );
      
      if (configDoc && selectedDocs.has(configDoc.id)) {
        try {
          const url = `${API}/api/metier/documents-engine/devis/${devisId}/documents/${docGen.id}/pdf`;
          const res = await fetch(url, { headers: authHeaders });
          
          if (res.ok) {
            const blob = await res.blob();
            const downloadUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = `${docGen.template_code}_v${docGen.version_document}.pdf`;
            a.click();
            URL.revokeObjectURL(downloadUrl);
          }
        } catch (error) {
          console.error("Erreur téléchargement:", error);
        }
      }
    }
    
    toast.success("Téléchargement en cours...");
  };

  const handlePrintAll = async () => {
    if (selectedDocs.size === 0) {
      toast.error("Sélectionnez au moins un document");
      return;
    }
    
    const authHeaders = getAuthHeaders();
    
    // Ouvrir les PDFs pour impression avec authentification
    for (const docGen of documentsGeneres) {
      const allDocs = [...docsAvantTravaux, ...docsApresTravaux];
      const configDoc = allDocs.find(d => 
        docGen.template_code?.includes(d.code) || d.id === docGen.type_document
      );
      
      if (configDoc && selectedDocs.has(configDoc.id)) {
        try {
          const url = `${API}/api/metier/documents-engine/devis/${devisId}/documents/${docGen.id}/apercu`;
          const response = await fetch(url, { headers: authHeaders });
          
          if (response.ok) {
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, "_blank");
            setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
          }
        } catch (err) {
          console.error("Erreur ouverture document:", err);
        }
      }
    }
  };

  // ==========================================================================
  // ENVOI DOCUSIGN
  // ==========================================================================

  const handleOpenSignatureDialog = (docGenere) => {
    if (!docusignConfigured) {
      toast.error("DocuSign n'est pas configuré", {
        description: "Configurez DocuSign dans Gouvernance > Intégrations"
      });
      return;
    }
    setDocToSign(docGenere);
    setShowSignatureDialog(true);
  };

  const handleSendForSignature = async () => {
    if (!docToSign) return;
    
    // Validation
    if (!signatureForm.signerEmail || !signatureForm.signerName) {
      toast.error("Remplissez l'email et le nom du signataire");
      return;
    }

    setSendingSignature(true);
    
    try {
      const authHeaders = getAuthHeaders();

      // 1. Récupérer le PDF du document
      // Si source="metier_devis" (doc TEF natif injecté dans documentsGeneres,
      // cf. fix BUG-01), utiliser download_url natif TEF — sinon route
      // documents-engine.
      const pdfUrl = (docToSign.source === "metier_devis" && docToSign.download_url)
        ? (docToSign.download_url.startsWith("http")
            ? docToSign.download_url
            : `${API}${docToSign.download_url}`)
        : `${API}/api/metier/documents-engine/devis/${devisId}/documents/${docToSign.id}/pdf`;
      const pdfRes = await fetch(pdfUrl, { headers: authHeaders });
      
      if (!pdfRes.ok) {
        throw new Error("Impossible de récupérer le PDF");
      }
      
      const pdfBlob = await pdfRes.blob();
      
      // 2. Créer le FormData pour l'envoi
      const formData = new FormData();
      formData.append("file", pdfBlob, `${docToSign.template_code}.pdf`);
      formData.append("signer_email", signatureForm.signerEmail);
      formData.append("signer_name", signatureForm.signerName);
      formData.append("email_subject", signatureForm.emailSubject || "Document à signer");
      formData.append("email_message", signatureForm.emailMessage || "");
      formData.append("devis_id", devisId);
      if (dossier?.id) {
        formData.append("dossier_id", dossier.id);
      }

      // 3. Envoyer à DocuSign (sans Content-Type pour FormData)
      const token = localStorage.getItem("session_token");
      const sendRes = await fetch(`${API}/api/docusign/send-pdf`, {
        method: "POST",
        headers: {
          "X-Tenant-ID": tenantId,
          ...(token && { "Authorization": `Bearer ${token}` })
        },
        body: formData
      });

      if (!sendRes.ok) {
        const errData = await sendRes.json().catch(() => ({}));
        throw new Error(errData.detail || "Erreur lors de l'envoi");
      }

      const result = await sendRes.json();
      
      toast.success("Document envoyé pour signature !", {
        description: `Email envoyé à ${signatureForm.signerEmail}`
      });
      
      setShowSignatureDialog(false);
      setDocToSign(null);
      loadData();
      
    } catch (error) {
      console.error("Erreur envoi signature:", error);
      toast.error("Erreur lors de l'envoi", {
        description: error.message
      });
    } finally {
      setSendingSignature(false);
    }
  };

  // ==========================================================================
  // VÉRIFIER SI UN DOCUMENT EST DÉJÀ GÉNÉRÉ
  // ==========================================================================

  const isDocumentGenere = (docConfig) => {
    // Aliases pour matcher les codes templates DB selon le tenant (ex: MANDAT_MPR ↔ MANDAT_ANAH_TEF_2025)
    const templateCodes = (docConfig.template_match || [docConfig.code]).map(c => c.toUpperCase());
    const typeCodes = (docConfig.type_match || [docConfig.code.toLowerCase()]);

    const templateGenere = documentsGeneres.some(dg => {
      const tc = (dg.template_code || "").toUpperCase();
      const td = (dg.type_document || "").toLowerCase();
      return templateCodes.some(c => tc.includes(c)) || typeCodes.includes(td);
    });

    // Vérifier dans les PDFs officiels générés
    const pdfGenere = pdfsGeneres[docConfig.code] !== undefined;

    return templateGenere || pdfGenere;
  };

  const getDocumentGenere = (docConfig) => {
    const templateCodes = (docConfig.template_match || [docConfig.code]).map(c => c.toUpperCase());
    const typeCodes = (docConfig.type_match || [docConfig.code.toLowerCase()]);

    // D'abord chercher dans les templates (priorité dernière version)
    const templateDoc = documentsGeneres.find(dg => {
      const tc = (dg.template_code || "").toUpperCase();
      const td = (dg.type_document || "").toLowerCase();
      return templateCodes.some(c => tc.includes(c)) || typeCodes.includes(td);
    });

    if (templateDoc) return templateDoc;

    // Sinon chercher dans les PDFs officiels ou devis
    const pdfDoc = pdfsGeneres[docConfig.code];
    if (pdfDoc) {
      return {
        id: docConfig.code,
        template_code: docConfig.code,
        download_url: pdfDoc.download_url,
        source: pdfDoc.source || "pdf_officiel",  // Préserver la source (devis ou pdf_officiel)
        generated_at: pdfDoc.generated_at
      };
    }

    return null;
  };

  // ==========================================================================
  // FILTRER DOCUMENTS APRÈS TRAVAUX PAR DÉLÉGATAIRE
  // ==========================================================================

  const getDocumentsApresTravaux = () => {
    if (!delegataireDossier) return docsApresTravaux;
    
    // Filtrer les templates disponibles pour ce délégataire
    const templatesDisponibles = templates
      .filter(t => t.phase === "apres_travaux")
      .filter(t => !t.delegataire || t.delegataire === delegataireDossier);
    
    // Mapper vers les documents config
    return docsApresTravaux.filter(doc => {
      const hasTemplate = templatesDisponibles.some(t => 
        t.code.includes(doc.code) || t.type_document === doc.code.toLowerCase()
      );
      return hasTemplate || doc.source !== "template";
    });
  };

  // ==========================================================================
  // RENDU
  // ==========================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  const documentsApresTravaux = getDocumentsApresTravaux();

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* En-tête avec actions */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-[var(--oh-text)] flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-cyan-400" />
              Préparer les documents à signer
            </h3>
            <p className="text-sm text-[var(--oh-text-muted)] mt-1">
              Sélectionnez les documents, visualisez les champs de signature, puis générez et téléchargez
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[var(--oh-text-muted)]">
              {selectedDocs.size} sélectionné(s)
            </Badge>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={loadData}
              className="border-[var(--oh-border)]"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Info délégataire */}
        {delegataireDossier && (
          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-cyan-300">
              Délégataire du dossier : <strong>{delegataireDossier}</strong>
            </span>
          </div>
        )}

        {/* Statut de signature DocuSign */}
        {signatureStatus?.has_signature && (
          <Card className="bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border-[var(--oh-accent)]" data-testid="docusign-status-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    signatureStatus.docusign_status === "completed" ? "bg-[var(--oh-success-bg)]" :
                    signatureStatus.docusign_status === "sent" || signatureStatus.docusign_status === "delivered" ? "bg-[var(--oh-info-bg)]" :
                    signatureStatus.docusign_status === "declined" || signatureStatus.docusign_status === "voided" ? "bg-[var(--oh-error-bg)]" :
                    "bg-[var(--oh-accent-bg)]"
                  }`}>
                    <FileSignature className={`w-5 h-5 ${
                      signatureStatus.docusign_status === "completed" ? "text-[var(--oh-success-text)]" :
                      signatureStatus.docusign_status === "sent" || signatureStatus.docusign_status === "delivered" ? "text-[var(--oh-info-text)]" :
                      signatureStatus.docusign_status === "declined" || signatureStatus.docusign_status === "voided" ? "text-[var(--oh-error-text)]" :
                      "text-[var(--oh-accent-text)]"
                    }`} />
                  </div>
                  <div>
                    <p className="text-[var(--oh-text)] font-medium">Signature DocuSign</p>
                    <p className="text-sm text-[var(--oh-text-muted)]">
                      Envoyé à : {signatureStatus.signer_email}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Badge className={`${
                    signatureStatus.docusign_status === "completed" ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]" :
                    signatureStatus.docusign_status === "sent" ? "bg-[var(--oh-info-bg)] text-[var(--oh-info-text)]" :
                    signatureStatus.docusign_status === "delivered" ? "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]" :
                    signatureStatus.docusign_status === "declined" ? "bg-[var(--oh-error-bg)] text-[var(--oh-error-text)]" :
                    signatureStatus.docusign_status === "voided" ? "bg-[var(--oh-bg-elevated)]/20 text-[var(--oh-text-muted)]" :
                    "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]"
                  }`} data-testid="docusign-status-badge">
                    {signatureStatus.docusign_status === "completed" ? "✓ Signé" :
                     signatureStatus.docusign_status === "sent" ? "En attente" :
                     signatureStatus.docusign_status === "delivered" ? "Email reçu" :
                     signatureStatus.docusign_status === "declined" ? "Refusé" :
                     signatureStatus.docusign_status === "voided" ? "Annulé" :
                     signatureStatus.docusign_status_label || signatureStatus.docusign_status || "En cours"}
                  </Badge>
                  
                  {signatureStatus.docusign_status === "completed" && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-[var(--oh-success-border)] text-[var(--oh-success-text)] hover:bg-[var(--oh-success-bg)]"
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API}/api/docusign/download/${signatureStatus.envelope_id}`, {
                            headers: getAuthHeaders()
                          });
                          if (response.ok) {
                            const blob = await response.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `document_signe_${signatureStatus.envelope_id}.pdf`;
                            a.click();
                            URL.revokeObjectURL(url);
                          }
                        } catch (err) {
                          toast.error("Erreur téléchargement");
                        }
                      }}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Télécharger signé
                    </Button>
                  )}
                </div>
              </div>
              
              {signatureStatus.sent_at && (
                <p className="text-xs text-[var(--oh-text-secondary)] mt-2">
                  Envoyé le : {new Date(signatureStatus.sent_at).toLocaleDateString('fr-FR', { 
                    day: '2-digit', month: '2-digit', year: 'numeric', 
                    hour: '2-digit', minute: '2-digit' 
                  })}
                  {signatureStatus.signed_at && (
                    <> · Signé le : {new Date(signatureStatus.signed_at).toLocaleDateString('fr-FR', { 
                      day: '2-digit', month: '2-digit', year: 'numeric', 
                      hour: '2-digit', minute: '2-digit' 
                    })}</>
                  )}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Section Avant Travaux */}
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <Collapsible 
            open={expandedPhase === "avant_travaux"} 
            onOpenChange={() => setExpandedPhase(expandedPhase === "avant_travaux" ? "" : "avant_travaux")}
          >
            <CardHeader className="cursor-pointer hover:bg-[var(--oh-bg-elevated)] transition-colors p-0">
              <div className="flex items-center justify-between p-4">
                <CollapsibleTrigger asChild>
                  <div className="flex items-center gap-3 flex-1 cursor-pointer">
                    {expandedPhase === "avant_travaux" ? (
                      <ChevronDown className="w-5 h-5 text-[var(--oh-text-muted)]" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-[var(--oh-text-muted)]" />
                    )}
                    <Clock className="w-5 h-5 text-[var(--oh-info-text)]" />
                    <CardTitle className="text-[var(--oh-text)]">Avant Travaux</CardTitle>
                    <Badge className="bg-[var(--oh-info-bg)] text-[var(--oh-info-text)]">
                      {docsAvantTravaux.length} documents
                    </Badge>
                  </div>
                </CollapsibleTrigger>
                <div className="flex items-center gap-2">
                  <span 
                    onClick={() => selectAllPhase("avant_travaux")}
                    className="text-xs text-[var(--oh-text-muted)] hover:text-[var(--oh-text)] cursor-pointer px-2 py-1 rounded hover:bg-[var(--oh-bg-elevated)]"
                  >
                    Tout sélectionner
                  </span>
                  <span 
                    onClick={() => deselectAllPhase("avant_travaux")}
                    className="text-xs text-[var(--oh-text-muted)] hover:text-[var(--oh-text)] cursor-pointer px-2 py-1 rounded hover:bg-[var(--oh-bg-elevated)]"
                  >
                    Tout désélectionner
                  </span>
                </div>
              </div>
            </CardHeader>
            
            <CollapsibleContent>
              <CardContent className="space-y-3 pt-0">
                {docsAvantTravaux.map(doc => (
                  <DocumentRow 
                    key={doc.id}
                    doc={doc}
                    isSelected={selectedDocs.has(doc.id)}
                    onToggle={() => toggleDocSelection(doc.id)}
                    isGenere={isDocumentGenere(doc)}
                    documentGenere={getDocumentGenere(doc)}
                    devisId={devisId}
                    tenantId={tenantId}
                    getAuthHeaders={getAuthHeaders}
                    docusignConfigured={docusignConfigured}
                    onSendSignature={handleOpenSignatureDialog}
                  />
                ))}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Section Après Travaux */}
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <Collapsible 
            open={expandedPhase === "apres_travaux"} 
            onOpenChange={() => setExpandedPhase(expandedPhase === "apres_travaux" ? "" : "apres_travaux")}
          >
            <CardHeader className="cursor-pointer hover:bg-[var(--oh-bg-elevated)] transition-colors p-0">
              <div className="flex items-center justify-between p-4">
                <CollapsibleTrigger asChild>
                  <div className="flex items-center gap-3 flex-1 cursor-pointer">
                    {expandedPhase === "apres_travaux" ? (
                      <ChevronDown className="w-5 h-5 text-[var(--oh-text-muted)]" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-[var(--oh-text-muted)]" />
                    )}
                    <CheckCircle2 className="w-5 h-5 text-[var(--oh-success-text)]" />
                    <CardTitle className="text-[var(--oh-text)]">Après Travaux</CardTitle>
                    <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]">
                      {documentsApresTravaux.length} documents
                    </Badge>
                  </div>
                </CollapsibleTrigger>
                <div className="flex items-center gap-2">
                  <span 
                    onClick={() => selectAllPhase("apres_travaux")}
                    className="text-xs text-[var(--oh-text-muted)] hover:text-[var(--oh-text)] cursor-pointer px-2 py-1 rounded hover:bg-[var(--oh-bg-elevated)]"
                  >
                    Tout sélectionner
                  </span>
                  <span 
                    onClick={() => deselectAllPhase("apres_travaux")}
                    className="text-xs text-[var(--oh-text-muted)] hover:text-[var(--oh-text)] cursor-pointer px-2 py-1 rounded hover:bg-[var(--oh-bg-elevated)]"
                  >
                    Tout désélectionner
                  </span>
                </div>
              </div>
            </CardHeader>
            
            <CollapsibleContent>
              <CardContent className="space-y-3 pt-0">
                {documentsApresTravaux.map(doc => (
                  <DocumentRow 
                    key={doc.id}
                    doc={doc}
                    isSelected={selectedDocs.has(doc.id)}
                    onToggle={() => toggleDocSelection(doc.id)}
                    isGenere={isDocumentGenere(doc)}
                    documentGenere={getDocumentGenere(doc)}
                    devisId={devisId}
                    tenantId={tenantId}
                    getAuthHeaders={getAuthHeaders}
                    docusignConfigured={docusignConfigured}
                    onSendSignature={handleOpenSignatureDialog}
                  />
                ))}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Barre d'actions fixe en bas */}
        {selectedDocs.size > 0 && (
          <div className="sticky bottom-4 bg-[var(--oh-bg-surface)] backdrop-blur border border-[var(--oh-border)] rounded-lg p-4 flex items-center justify-between shadow-xl">
            <div className="flex items-center gap-2 text-sm text-[var(--oh-text-muted)]">
              <CheckSquare className="w-4 h-4 text-cyan-400" />
              <span>{selectedDocs.size} document(s) sélectionné(s)</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handlePrintAll}
                className="border-[var(--oh-border)]"
              >
                <Printer className="w-4 h-4 mr-2" />
                Imprimer
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleDownloadAll}
                className="border-[var(--oh-border)]"
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
              <Button 
                size="sm"
                onClick={handlePrepareGeneration}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                <FileSignature className="w-4 h-4 mr-2" />
                Préparer & Générer
              </Button>
            </div>
          </div>
        )}

        {/* Dialog de génération */}
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent className="max-w-2xl bg-[var(--oh-bg-surface)] border-[var(--oh-border)]" aria-describedby="generate-dialog-desc">
            <DialogHeader>
              <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-cyan-400" />
                Préparer les documents pour signature
              </DialogTitle>
              <DialogDescription id="generate-dialog-desc" className="text-[var(--oh-text-muted)]">
                Vérifiez les champs de signature requis avant de générer
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {docsToGenerate.map(doc => (
                <div key={doc.id} className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-cyan-400" />
                      <span className="text-[var(--oh-text)] font-medium">{doc.nom}</span>
                    </div>
                    <Badge className={
                      doc.source === "template" ? "bg-[var(--oh-stat-cyan-bg)] text-[var(--oh-stat-cyan-text)]" :
                      doc.source === "modele_officiel" ? "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]" :
                      "bg-[var(--oh-info-bg)] text-[var(--oh-info-text)]"
                    }>
                      {doc.source === "template" ? "À générer" :
                       doc.source === "modele_officiel" ? "Modèle officiel" :
                       "Devis"}
                    </Badge>
                  </div>
                  
                  <p className="text-xs text-[var(--oh-text-secondary)]">{doc.description}</p>
                  
                  {/* Champs de signature */}
                  <div className="bg-[var(--oh-bg-surface)] rounded p-3">
                    <p className="text-xs text-[var(--oh-text-muted)] mb-2 flex items-center gap-1">
                      <PenTool className="w-3 h-3" />
                      Champs de signature requis :
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {doc.champsSignature.map((champ, idx) => {
                        const Icon = SIGNATURE_ICONS[champ.type] || FileText;
                        return (
                          <Tooltip key={idx}>
                            <TooltipTrigger>
                              <Badge variant="outline" className="text-xs border-[var(--oh-border)] flex items-center gap-1">
                                <Icon className="w-3 h-3" />
                                {champ.label}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              Position : {champ.position}
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setShowGenerateDialog(false)}
                className="border-[var(--oh-border)]"
              >
                Annuler
              </Button>
              <Button 
                onClick={handleGenerateAll}
                disabled={generating}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Génération...
                  </>
                ) : (
                  <>
                    <FileCheck className="w-4 h-4 mr-2" />
                    Générer {docsToGenerate.length} document(s)
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog DocuSign */}
        <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
          <DialogContent className="max-w-md bg-[var(--oh-bg-surface)] border-[var(--oh-border)]" aria-describedby="signature-dialog-desc">
            <DialogHeader>
              <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
                <Send className="w-5 h-5 text-[var(--oh-accent-text)]" />
                Envoyer pour signature électronique
              </DialogTitle>
              <DialogDescription id="signature-dialog-desc" className="text-[var(--oh-text-muted)]">
                Le document sera envoyé via DocuSign au signataire
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Document à signer */}
              {docToSign && (
                <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-3 flex items-center gap-3">
                  <FileText className="w-5 h-5 text-cyan-400" />
                  <div>
                    <p className="text-[var(--oh-text)] font-medium text-sm">{docToSign.template_code}</p>
                    <p className="text-xs text-[var(--oh-text-muted)]">v{docToSign.version_document}</p>
                  </div>
                </div>
              )}
              
              {/* Formulaire */}
              <div className="space-y-3">
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-sm">Email du signataire *</Label>
                  <Input
                    type="email"
                    value={signatureForm.signerEmail}
                    onChange={(e) => setSignatureForm(prev => ({ ...prev, signerEmail: e.target.value }))}
                    placeholder="client@example.com"
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-sm">Nom du signataire *</Label>
                  <Input
                    value={signatureForm.signerName}
                    onChange={(e) => setSignatureForm(prev => ({ ...prev, signerName: e.target.value }))}
                    placeholder="Jean Dupont"
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-sm">Sujet de l'email</Label>
                  <Input
                    value={signatureForm.emailSubject}
                    onChange={(e) => setSignatureForm(prev => ({ ...prev, emailSubject: e.target.value }))}
                    placeholder="Documents à signer"
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-sm">Message (optionnel)</Label>
                  <Input
                    value={signatureForm.emailMessage}
                    onChange={(e) => setSignatureForm(prev => ({ ...prev, emailMessage: e.target.value }))}
                    placeholder="Merci de signer ce document..."
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)] mt-1"
                  />
                </div>
              </div>
              
              {/* Info DocuSign */}
              <div className="bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)] rounded-lg p-3 flex items-start gap-2">
                <Mail className="w-4 h-4 text-[var(--oh-accent-text)] mt-0.5" />
                <p className="text-xs text-[var(--oh-accent-text)]">
                  Le signataire recevra un email avec un lien sécurisé pour signer le document électroniquement via DocuSign.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => { setShowSignatureDialog(false); setDocToSign(null); }}
                className="border-[var(--oh-border)]"
              >
                Annuler
              </Button>
              <Button 
                onClick={handleSendForSignature}
                disabled={sendingSignature || !signatureForm.signerEmail || !signatureForm.signerName}
                className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
              >
                {sendingSignature ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer pour signature
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

// =============================================================================
// COMPOSANT LIGNE DOCUMENT
// =============================================================================

function DocumentRow({ doc, isSelected, onToggle, isGenere, documentGenere, devisId, tenantId, getAuthHeaders, docusignConfigured, onSendSignature }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`
      border rounded-lg p-3 transition-all
      ${isSelected ? "border-cyan-500/50 bg-cyan-500/5" : "border-[var(--oh-border)] bg-[var(--oh-bg-elevated)]"}
      ${doc.optionnel ? "opacity-75" : ""}
    `}>
      <div className="flex items-center gap-3">
        {/* Checkbox */}
        <Checkbox 
          checked={isSelected}
          onCheckedChange={onToggle}
          className="border-[var(--oh-border)]"
        />
        
        {/* Info document */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[var(--oh-text)] font-medium text-sm">{doc.nom}</span>
            {doc.optionnel && (
              <Badge variant="outline" className="text-xs border-[var(--oh-border)] text-[var(--oh-text-muted)]">
                Optionnel
              </Badge>
            )}
            {isGenere && (
              <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] text-xs">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Généré
              </Badge>
            )}
            <Badge variant="outline" className="text-xs border-[var(--oh-border)] text-[var(--oh-text-secondary)]">
              {doc.source === "template" ? "Template" :
               doc.source === "modele_officiel" ? "Modèle officiel" :
               "Devis"}
            </Badge>
          </div>
          <p className="text-xs text-[var(--oh-text-secondary)] mt-0.5">{doc.description}</p>
        </div>
        
        {/* Bouton pour voir les champs de signature */}
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className="text-[var(--oh-text-muted)] hover:text-[var(--oh-text)]"
        >
          {expanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </Button>

        {/* Bouton "Cerfa vierge" — affiche UNIQUEMENT si aucun doc rempli
            n'existe (sinon piege "PDF blanc" cf. BUG-01 FIX C audit). */}
        {doc.cerfa_source && !isGenere && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  const authHeaders = getAuthHeaders();
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/metier/tef/cerfa-source/${doc.cerfa_source}`;
                  try {
                    const res = await fetch(url, { headers: authHeaders });
                    if (res.ok) {
                      const blob = await res.blob();
                      const blobUrl = URL.createObjectURL(blob);
                      const a = window.document.createElement("a");
                      a.href = blobUrl;
                      a.download = `${doc.nom.replace(/[^a-zA-Z0-9_-]/g, "_")}.pdf`;
                      window.document.body.appendChild(a);
                      a.click();
                      window.document.body.removeChild(a);
                      URL.revokeObjectURL(blobUrl);
                    } else {
                      const err = await res.json().catch(() => ({}));
                      toast.error(err.detail || "Erreur lors du téléchargement du Cerfa vierge");
                    }
                  } catch (e) {
                    toast.error("Erreur: " + e.message);
                  }
                }}
                className="text-[var(--oh-info-text)] hover:text-[var(--oh-info-text)]"
                data-testid={`cerfa-blank-${doc.id}`}
              >
                <FileText className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Télécharger le Cerfa vierge à remplir</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Actions si généré */}
        {isGenere && documentGenere && (
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={async () => {
                    // Pour tous les documents, utiliser fetch avec headers pour éviter l'erreur Tenant
                    const authHeaders = getAuthHeaders();
                    let url;
                    if (documentGenere.source === "pdf_officiel" || documentGenere.source === "devis" || documentGenere.source === "metier_devis") {
                      url = documentGenere.download_url.startsWith("http")
                        ? documentGenere.download_url
                        : `${process.env.REACT_APP_BACKEND_URL}${documentGenere.download_url}`;
                    } else {
                      url = `${process.env.REACT_APP_BACKEND_URL}/api/metier/documents-engine/devis/${devisId}/documents/${documentGenere.id}/apercu?tenant=${encodeURIComponent(tenantId)}`;
                    }
                    
                    try {
                      const res = await fetch(url, { headers: authHeaders });
                      if (res.ok) {
                        const blob = await res.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        window.open(blobUrl, "_blank");
                      } else {
                        toast.error("Erreur lors de l'aperçu du document");
                      }
                    } catch (e) {
                      toast.error("Erreur: " + e.message);
                    }
                  }}
                  className="text-[var(--oh-accent-text)] hover:text-[var(--oh-accent-text)]"
                >
                  <Eye className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Aperçu</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={async () => {
                    // URL différente selon le type de document
                    const authHeaders = getAuthHeaders();
                    let url;
                    if (documentGenere.source === "pdf_officiel" || documentGenere.source === "devis" || documentGenere.source === "metier_devis") {
                      url = documentGenere.download_url.startsWith("http")
                        ? documentGenere.download_url
                        : `${process.env.REACT_APP_BACKEND_URL}${documentGenere.download_url}`;
                    } else {
                      url = `${process.env.REACT_APP_BACKEND_URL}/api/metier/documents-engine/devis/${devisId}/documents/${documentGenere.id}/pdf`;
                    }
                    const res = await fetch(url, { headers: authHeaders });
                    if (res.ok) {
                      const blob = await res.blob();
                      const downloadUrl = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = downloadUrl;
                      a.download = `${documentGenere.template_code}.pdf`;
                      a.click();
                      URL.revokeObjectURL(downloadUrl);
                    }
                  }}
                  className="text-[var(--oh-success-text)] hover:text-[var(--oh-success-text)]"
                >
                  <Download className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Télécharger</TooltipContent>
            </Tooltip>
            {/* Bouton DocuSign - uniquement si configuré */}
            {docusignConfigured && onSendSignature && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => onSendSignature(documentGenere)}
                    className="text-[var(--oh-accent-text)] hover:text-[var(--oh-accent-text)]"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Envoyer pour signature (DocuSign)</TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      
      {/* Détails des champs de signature */}
      {expanded && (
        <div className="mt-3 pl-8 pt-3 border-t border-[var(--oh-border)]">
          <p className="text-xs text-[var(--oh-text-muted)] mb-2 flex items-center gap-1">
            <PenTool className="w-3 h-3" />
            Champs de signature :
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {doc.champsSignature.map((champ, idx) => {
              const Icon = SIGNATURE_ICONS[champ.type] || FileText;
              return (
                <div key={idx} className="flex items-center gap-2 text-xs text-[var(--oh-text-muted)] bg-[var(--oh-bg-surface)] rounded px-2 py-1">
                  <Icon className="w-3 h-3 text-cyan-400" />
                  <span>{champ.label}</span>
                  <span className="text-[var(--oh-text-secondary)] ml-auto">({champ.position})</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
